import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import lockr from 'lockr';
import axios from 'axios';
import { fetchApi, fetchApiAuth } from '../utils/api';
import {
    ResponseProps,
    Indicator,
    Category,
    ExportIndicatorsPayload,
    GetIndicatorsPayload,
    TimeSeriesApiResponse,
    RecalculateComputedIndicatorPayload,
    RecalculateComputedIndicatorResponse,
    RecalculateComputedIndicatorAllPayload,
    RecalculateComputedIndicatorAllResponse,
    UnconfirmIndicatorValuePayload,
    UnconfirmIndicatorValueResponse,
} from './types';
import constants from '../constants';

const {
    LOCALSTORAGE_EXPIRES_KEY,
    AUTH_KEY,
} = constants;

export const updateIndicator = createAsyncThunk(
    'indicator/update-indicator',
    async (payload:any) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/update-indicator',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const createIndicator = createAsyncThunk(
    'indicator/create-indicator',
    async (payload:Indicator) => {
        const response:ResponseProps = await fetchApiAuth({
            method: 'POST',
            url: 'v1/indicator/create-indicator',
            body: payload,
        });
        return response.data;
    },
);

export const createIndicatorCategory = createAsyncThunk(
    'indicator/create-indicator-category',
    async (payload:Category) => {
        const response:ResponseProps = await fetchApiAuth({
            method: 'POST',
            url: 'v1/indicator/create-indicator-category',
            body: payload,
        });
        return response.data;
    },
);

export const updateIndicatorCategory = createAsyncThunk(
    'indicator/update-indicator-category',
    async (payload:Category) => {
        const response:ResponseProps = await fetchApiAuth({
            method: 'POST',
            url: 'v1/indicator/update-indicator-category',
            body: payload,
        });
        return response.data;
    },
);

export const getIndicators = createAsyncThunk(
    'indicator/get-indicators',
    async (payload: any = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/get-indicators',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSchoolIndicators = createAsyncThunk(
    'indicator/get-school-indicators',
    async (payload: any = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/get-school-indicators',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSchoolIndicatorsCalendar = createAsyncThunk(
    'indicator/get-school-indicators-calendar',
    async (payload: any = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/get-school-indicators-calendar',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const updateSchoolIndicators = createAsyncThunk(
    'indicator/update-school-indicators',
    async (payload: any = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/update-school-indicators',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSchoolIndicatorLogs = createAsyncThunk(
    'indicator/get-school-indicator-logs',
    async (payload: any = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/get-school-indicator-logs',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getIndicatorCategories = createAsyncThunk(
    'indicator/get-indicator-categories',
    async (payload: GetIndicatorsPayload = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/get-indicator-categories',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSchoolIndicatorCategories = createAsyncThunk(
    'indicator/get-school-indicator-categories',
    async (payload: GetIndicatorsPayload = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/get-school-indicator-categories',
                body: payload,
            });
            return { ...response.data, ...payload };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const deleteIndicator = createAsyncThunk(
    'indicator/delete-indicator',
    async (payload: { id: number }) => {
        try {
            const response: ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/delete-indicator',
                body: payload,
            });
            return { ...payload, message: response.data?.message };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const deleteIndicatorCategory = createAsyncThunk(
    'indicator/delete-indicator-category',
    async (payload:Category) => {
        try {
            await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/delete-indicator-category',
                body: payload,
            });
            return payload;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getCategoryIndicatorsReport = createAsyncThunk(
    'indicator/get-category-indicators-report',
    async (payload: { category_id: number; date: string }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/get-category-indicators-report',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const compareSchools = createAsyncThunk(
    'indicator/compare-schools',
    async (payload: {
        school_ids: number[];
        category_id?: number;
        indicator_ids?: number[];
        special_indicators?: number[];
        date_from?: string;
        date_to?: string;
    }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/compare-schools',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const exportSchoolsComparison = createAsyncThunk(
    'indicator/export-schools-comparison',
    async (payload: {
        school_ids: number[];
        category_id?: number;
        indicator_ids?: number[];
        special_indicators?: number[];
        date_from?: string;
        date_to?: string;
    }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/compare-schools-export',
                body: payload,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const exportIndicators = createAsyncThunk(
    'indicator/export-indicators',
    async (payload: ExportIndicatorsPayload = {}) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/export-indicators',
                body: payload,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const axiosError = error;
                const data = axiosError.response?.data;

                // When responseType is 'blob', backend errors may arrive as Blob(JSON)
                const tryParseBlobMessage = async () => {
                    try {
                        if (data instanceof Blob) {
                            const text = await data.text();
                            const json = JSON.parse(text);
                            return (json?.message as string) || 'Something went wrong';
                        }
                    } catch (e) {
                        // ignore
                    }
                    const hasMessage = (v: unknown): v is { message?: unknown } => (
                        typeof v === 'object' && v !== null && 'message' in v
                    );
                    const fallback = axiosError.response?.data;
                    if (hasMessage(fallback) && typeof fallback.message === 'string' && fallback.message.trim()) {
                        return fallback.message;
                    }
                    return 'Something went wrong';
                };

                const errorMessage = await tryParseBlobMessage();
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const exportEducationScope = createAsyncThunk(
    'indicator/export-education-scope',
    async (payload: {
        scope: 'education_center' | 'education_department';
        category_id: number;
        date_from: string;
        date_to: string;
        format: 'xlsx';
    }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/compare-education-scope-export',
                body: payload,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const exportEducationScopePdf = createAsyncThunk(
    'indicator/export-education-scope-pdf',
    async (payload: {
        scope: 'education_center' | 'education_department';
        category_id: number;
        date_from: string;
        date_to: string;
    }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/compare-education-scope-pdf',
                body: payload,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const exportSchoolsComparisonPdf = createAsyncThunk(
    'indicator/export-schools-comparison-pdf',
    async (payload: {
        school_ids: number[];
        category_id?: number;
        indicator_ids?: number[];
        special_indicators?: number[];
        date_from?: string;
        date_to?: string;
    }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/compare-schools-pdf',
                body: payload,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const compareSchoolsTimeSeries = createAsyncThunk(
    'indicator/compare-schools-time-series',
    async (payload: {
        school_ids: number[];
        category_id?: number;
        indicator_ids?: number[];
        special_indicators?: number[];
        date_from?: string;
        date_to?: string;
    }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/compare-schools-time-series',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getEducationScopeTimeSeries = createAsyncThunk(
    'indicator/get-education-scope-time-series',
    async (payload: {
        scope: string;
        category_id: number;
        date_from: string;
        date_to: string;
    }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/compare-education-scope-time-series',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const generateIndicatorsImportTemplate = createAsyncThunk(
    'indicator/generate-indicators-import-template',
    async (payload: {
        school_ids: number[];
        category_id?: number;
        indicator_ids?: number[];
    }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/generate-indicators-import-template',
                body: payload,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const importIndicatorsValues = createAsyncThunk(
    'indicator/import-indicators-values',
    async (payload: {
        file: File;
        date: string;
    }) => {
        try {
            const formData = new FormData();
            formData.append('file', payload.file);
            formData.append('date', payload.date);

            console.log('API: Sending FormData with:', {
                file: payload.file.name,
                fileSize: payload.file.size,
                fileType: payload.file.type,
                date: payload.date,
            });

            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/import-indicator-values',
                body: formData,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getIndicatorSchoolValues = createAsyncThunk(
    'indicator/get-indicator-school-values',
    async (payload: { indicator_id: number; date: string }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/get-indicator-school-values',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const unconfirmIndicatorValue = createAsyncThunk(
    'indicator/unconfirm-indicator-value',
    async (payload: UnconfirmIndicatorValuePayload) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/unconfirm-indicator-value',
                body: payload,
            });
            return response.data as UnconfirmIndicatorValueResponse;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Nepavyko atšaukti rodiklio patvirtinimo';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const recalculateComputedIndicatorAll = createAsyncThunk(
    'indicator/recalculate-computed-indicator-all',
    async (payload: RecalculateComputedIndicatorAllPayload) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/recalculate-computed-indicator-all',
                body: payload,
            });
            return response.data as RecalculateComputedIndicatorAllResponse;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
                const validationMessages = data?.errors
                    ? Object.values(data.errors).flat().filter(Boolean)
                    : [];
                const errorMessage = validationMessages.join(' ')
                    || data?.message
                    || 'Nepavyko perskaičiuoti rodiklio reikšmių';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const recalculateComputedIndicator = createAsyncThunk(
    'indicator/recalculate-computed-indicator',
    async (payload: RecalculateComputedIndicatorPayload) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/recalculate-computed-indicator',
                body: payload,
            });
            return response.data as RecalculateComputedIndicatorResponse;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Nepavyko perskaičiuoti rodiklio';
                notification.error({
                    message: 'Klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const bulkUpdateIndicatorsStatus = createAsyncThunk(
    'indicator/bulk-update-indicators-status',
    async (payload: {
        indicator_ids: number[];
        status: number;
    }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/indicator/bulk-update-indicators-status',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);
